import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from './useApiClient.ts';
import { toErrorMessage } from '@metron/shared';

/**
 * Customer activation data returned from the API
 */
export interface CustomerActivation {
  id: number;
  waterscopeAccountName: string;
  accountType: string;
  waterscopeAccountId: string | null;
  metronId: string | null;
  salesforceId: string | null;
  active: boolean;
}

interface ListCustomersActivationResponse {
  customers: CustomerActivation[];
}

interface UpdateCustomerActiveStatusResponse {
  id: number;
  active: boolean;
}

const CUSTOMER_ACTIVATION_QUERY_KEY = ['customer-activation-status'] as const;

export function useCustomerActivation() {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  // Fetch customers activation status
  const {
    data: customers = [],
    isLoading,
    error: queryError,
  } = useQuery({
    queryKey: CUSTOMER_ACTIVATION_QUERY_KEY,
    queryFn: async () => {
      const data = await apiClient.get<ListCustomersActivationResponse>(
        'customer-activation-status',
      );
      return data.customers;
    },
  });

  // Update customer active status mutation with optimistic updates
  const updateMutation = useMutation({
    mutationFn: async ({ customerId, active }: { customerId: number; active: boolean }) => {
      return await apiClient.put<UpdateCustomerActiveStatusResponse>(
        `customer-activation-status/${customerId}`,
        { active },
      );
    },
    onMutate: async ({ customerId, active }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: CUSTOMER_ACTIVATION_QUERY_KEY });

      // Snapshot the previous value
      const previousCustomers = queryClient.getQueryData<CustomerActivation[]>(
        CUSTOMER_ACTIVATION_QUERY_KEY,
      );

      // Optimistically update to the new value
      queryClient.setQueryData<CustomerActivation[]>(
        CUSTOMER_ACTIVATION_QUERY_KEY,
        (old) =>
          old?.map((customer) => customer.id === customerId ? { ...customer, active } : customer) ??
            [],
      );

      // Return a context object with the snapshotted value
      return { previousCustomers };
    },
    onError: (_err, _variables, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousCustomers) {
        queryClient.setQueryData(CUSTOMER_ACTIVATION_QUERY_KEY, context.previousCustomers);
      }
    },
    onSettled: () => {
      // Always refetch after error or success to ensure cache is in sync with server
      queryClient.invalidateQueries({ queryKey: CUSTOMER_ACTIVATION_QUERY_KEY });
    },
  });

  // Error handling - convert query error to string for backward compatibility
  const error = queryError ? (toErrorMessage(queryError, 'An error occurred')) : null;

  return {
    customers,
    isLoading,
    error,
    updateActiveStatus: updateMutation.mutate,
    isUpdating: updateMutation.isPending,
    updateError: updateMutation.error,
  };
}
